import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { faqs } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiChevronDown, FiTruck, FiRefreshCw, FiMessageCircle } from 'react-icons/fi';

const AccordionItem = ({ faq, isOpen, onClick }) => {
  return (
    <div className="border border-gray-100 rounded-2xl mb-4 bg-white overflow-hidden">
      <button
        className="w-full px-6 py-4 flex justify-between items-center text-left"
        onClick={onClick}
      >
        <span className="font-bold text-lg">{faq.q}</span>
        <SafeIcon 
          icon={FiChevronDown} 
          className={`text-xl text-bloom-coral transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-6 pb-4 text-gray-600"
          >
            {faq.a}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Support() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-16">
      
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4">How can we help?</h1>
        <p className="text-lg text-gray-600">Find answers, shipping details, or reach out to our friendly team.</p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { icon: FiTruck, title: 'Shipping Info', desc: 'Track orders & delivery times', color: 'text-bloom-mint', bg: 'bg-bloom-mint/10' },
          { icon: FiRefreshCw, title: 'Returns', desc: '30-day happiness guarantee', color: 'text-bloom-coral', bg: 'bg-bloom-coral/10' },
          { icon: FiMessageCircle, title: 'Contact Us', desc: 'We usually reply in 2 hours', color: 'text-bloom-yellow', bg: 'bg-bloom-yellow/20' }
        ].map((item, idx) => (
          <div key={idx} className="bg-white p-6 rounded-3xl shadow-sm text-center flex flex-col items-center hover:shadow-md transition-shadow cursor-pointer">
            <div className={`w-14 h-14 rounded-full ${item.bg} ${item.color} flex items-center justify-center mb-4 text-2xl`}>
              <SafeIcon icon={item.icon} />
            </div>
            <h3 className="font-bold text-lg mb-1">{item.title}</h3>
            <p className="text-sm text-gray-500">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* FAQ Section */}
      <div>
        <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, idx) => (
            <AccordionItem 
              key={idx} 
              faq={faq} 
              isOpen={openFaq === idx} 
              onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
            />
          ))}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-bloom-mint/20 rounded-[3rem] p-10 text-center max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
        <p className="text-gray-700 mb-6">Our support team is available Monday to Friday, 9am - 5pm EST.</p>
        <button className="bg-bloom-text text-white px-8 py-3 rounded-full font-bold hover:bg-gray-800 transition-colors">
          Send a Message
        </button>
      </div>

    </div>
  );
}